import type { ArtMetadata } from "../schedule-presets/art-metadata";
import type { MetadataDay } from "../metadata-history/metadata-targets";
import type { MetadataEventPayload } from "../metadata-history/metadata-events";
import type { BuilderDayLayout } from "./day-layouts";
import { layoutCanvasPoint, layoutLocalPoint } from "../schedule-art/layout-coordinates";
import { weekdayKeys } from "../schedule-time/schedule-dates";
import { useHistoryStore } from "../../stores/history-store";
import { builderSessionId } from "../../stores/metadata-builder-store";
import type { AppHistoryAction } from "../schedule-history/app-history-actions";

type DateAnchors = BuilderDayLayout["anchors"];

function dateSetupAnchors(events: AppHistoryAction[], undoneIds: string[]) {
  const setup = events.findLast(action => {
    if (action.domain !== "metadata" || action.sessionId !== builderSessionId) return false;
    if (undoneIds.includes(action.event.id)) return false;
    return action.event.type === "day-layout/set" && action.event.dates !== undefined;
  });
  if (setup?.domain !== "metadata" || setup.event.type !== "day-layout/set") return null;
  return setup.event.layout.anchors;
}

// Use the original date setup, so later content-anchor moves leave dates fixed.
export function getBuilderDateAnchors() {
  const history = useHistoryStore.getState();
  return dateSetupAnchors(history.events, history.undoneIds);
}

export function useBuilderDateAnchors() {
  return useHistoryStore(state => dateSetupAnchors(state.events, state.undoneIds));
}

export function dayDateOffset(metadata: ArtMetadata, anchors: DateAnchors, day: MetadataDay) {
  const original = metadata.days[day];
  const canvasPoint = layoutCanvasPoint(original.date.offset, original.origin, original.rotation);
  return layoutLocalPoint(canvasPoint, anchors[day], original.rotation).map(Math.round);
}

export function dayDateMetadataOffset(metadata: ArtMetadata, anchors: DateAnchors, day: MetadataDay, point: number[]) {
  const original = metadata.days[day];
  const canvasPoint = layoutCanvasPoint(point, anchors[day], original.rotation);
  return layoutLocalPoint(canvasPoint, original.origin, original.rotation).map(Math.round);
}

export function moveSharedDayDate(metadata: ArtMetadata, anchors: DateAnchors, day: MetadataDay, point: number[]): MetadataEventPayload {
  const original = metadata.days[day];
  const canvasPoint = layoutCanvasPoint(point, original.origin, original.rotation);
  const sharedOffset = layoutLocalPoint(canvasPoint, anchors[day], original.rotation);
  const dates = Object.fromEntries(weekdayKeys.map(key => [key, {
    ...metadata.days[key].date,
    offset: dayDateMetadataOffset(metadata, anchors, key, sharedOffset),
  }]));
  return { type: "day-dates/set", dates, styles: {}, formats: {} };
}

export function rotateSharedDayDate(metadata: ArtMetadata, rotation: number): MetadataEventPayload {
  const dates = Object.fromEntries(weekdayKeys.map(day => [day, { ...metadata.days[day].date, rotation }]));
  return { type: "day-dates/set", dates, styles: {}, formats: {} };
}
